import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { format } from 'date-fns'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Spinner } from '../spinner'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { CreateUserTokenModal } from './create-user-token-modal'
import { UserTokenRevealModal } from './user-token-reveal-modal'
import { PermissionChips, TokenCard, TokenCardGrid } from '../tokens/token-card'
import { userTokenApi } from 'src/services/user-tokens/api'
import { userTokenQueries, UserTokenCreated } from 'src/services/user-tokens'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import type { TFunction } from 'i18next'

function formatDate(iso: string | null, t: TFunction): string {
  if (!iso) return t('userTokens.never')
  try {
    return format(new Date(iso), 'MMM d, yyyy')
  } catch {
    return '—'
  }
}

export const UserTokensSection: React.FC = () => {
  const { t } = useTranslation()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [revealed, setRevealed] = useState<UserTokenCreated | null>(null)

  const { data, isLoading } = useQuery(userTokenQueries.list())
  const tokens = data?.results ?? []

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (tokenId: string) => userTokenApi.remove(tokenId),
    queryKey: userTokenQueries.all(),
  })

  return (
    <div
      className="rounded-lg border border-border bg-surface p-6 shadow-sm"
      data-testid="user-tokens-section"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-text-primary">{t('userTokens.title')}</h3>
          <p className="mt-1 text-sm text-text-placeholder">{t('userTokens.description')}</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsCreateOpen(true)}
          icon={<PlusIcon className="h-4 w-4" />}
          data-testid="create-user-token-button"
        >
          {t('userTokens.createToken')}
        </Button>
      </div>

      <div
        className="border-warning/40 bg-warning/10 mb-4 rounded-md border p-3 text-sm text-text-secondary"
        data-testid="user-tokens-mcp-warning"
      >
        {t('userTokens.mcpUserTokenWarning')}{' '}
        <Link to="/organization-settings" className="font-medium text-primary underline">
          {t('userTokens.mcpBotTokensLink')}
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8" data-testid="user-tokens-loading">
          <Spinner size="md" />
        </div>
      ) : tokens.length === 0 ? (
        <div
          className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
          data-testid="user-tokens-empty"
        >
          {t('userTokens.empty')}
        </div>
      ) : (
        <TokenCardGrid testId="user-tokens-list">
          {tokens.map((token) => {
            const effective = new Set(token.effectivePermissions)
            const hasIneffective = token.permissions.some((p) => !effective.has(p))
            return (
              <TokenCard
                key={token.id}
                testId={`user-token-row-${token.id}`}
                name={token.name}
                prefix={token.prefix}
                permissionsTitle={t('userTokens.permissions')}
                permissionsTestId={`user-token-permissions-${token.id}`}
                permissions={
                  <>
                    <PermissionChips
                      permissions={token.permissions}
                      otherLabel={t('userTokens.otherPermissions')}
                      emptyText={t('userTokens.noPermissions')}
                      groupTestIdPrefix={`user-token-permission-chips-${token.id}`}
                      chipTestId={(codename) => `user-token-perm-${token.id}-${codename}`}
                      effective={effective}
                      ineffectiveTitle={(label) =>
                        t('userTokens.permissionNotEffective', { label })
                      }
                    />
                    {hasIneffective && (
                      <p
                        className="mt-1.5 text-xs text-text-tertiary"
                        data-testid={`user-token-ineffective-note-${token.id}`}
                      >
                        {t('userTokens.ineffectiveNote')}
                      </p>
                    )}
                  </>
                }
                meta={[
                  { label: t('userTokens.lastUsed'), value: formatDate(token.lastUsedAt, t) },
                  { label: t('userTokens.expires'), value: formatDate(token.expiresAt, t) },
                  { label: t('userTokens.created'), value: formatDate(token.created, t) },
                ]}
                revokeAriaLabel={t('userTokens.revokeToken', { name: token.name })}
                revokeTitle={t('userTokens.revoke')}
                revokeTestId={`revoke-user-token-${token.id}`}
                onRevoke={() => requestDelete(token.id, token.name)}
              />
            )
          })}
        </TokenCardGrid>
      )}

      {/* Revoke confirmation dialog */}
      <ConfirmDeleteDialog {...dialogProps} />

      <CreateUserTokenModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(created) => {
          setIsCreateOpen(false)
          setRevealed(created)
        }}
      />

      <UserTokenRevealModal
        token={revealed?.token ?? null}
        tokenName={revealed?.name ?? ''}
        onDismissConfirmed={() => setRevealed(null)}
      />
    </div>
  )
}
